import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateUtils';

interface FullRecordRequestPayloadProps {
  payload: Record<string, unknown>;
  i18n: { _: (d: { id: string; message: string }) => string };
  /** Locale for the due-date rendering; defaults to the app locale at the call site. */
  locale?: string;
}

/**
 * Local copy of WorkflowPayloadDetail's FieldRow. This is a detail panel, not a
 * data table, so BaseEditableTable does not apply and no raw <table> is used.
 */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Read a payload field as a non-blank string, or undefined. */
function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Renders a `portfolio_full_record_request` payload (issue #1266): a parent
 * asked for everything the organisation holds about their child, and the answer
 * is owed within one month.
 *
 * IT MUST NEVER RENDER A NOTE BODY. The payload deliberately carries none --
 * only `has_note`, the EXISTENCE of the parent's text. The words themselves
 * live on the request row, behind the admin-only staff route, because the
 * workflow inbox is a wider surface than that route.
 */
export function FullRecordRequestPayload({
  payload,
  i18n,
  locale,
}: FullRecordRequestPayloadProps) {
  const childName = readString(payload, 'child_name');
  const dueAt = readString(payload, 'due_at');
  const hasNote = payload.has_note === true;

  return (
    <div className="space-y-4">
      <FieldRow label={i18n._({ id: 'workflow.payload.childName', message: 'Child' })}>
        {/* An em dash, never a raw id: the name is empty when the child could
            not be loaded, and the obligation is raised regardless. */}
        <p className="text-sm">{childName ?? '—'}</p>
      </FieldRow>

      {dueAt && (
        <FieldRow
          label={i18n._({
            id: 'workflow.payload.fullRecordDueAt',
            message: 'Answer due',
          })}
        >
          <p className="text-sm">{formatDate(dueAt, locale)}</p>
        </FieldRow>
      )}

      {hasNote && (
        <FieldRow
          label={i18n._({
            id: 'workflow.payload.fullRecordNote',
            message: 'Parent note',
          })}
        >
          {/* Text-bearing, so the fact is never conveyed by colour alone. */}
          <Badge variant="secondary" data-testid="full-record-payload-note-flag">
            {i18n._({
              id: 'workflow.payload.fullRecordHasNote',
              message: 'A written note was provided. Open the request to read it.',
            })}
          </Badge>
        </FieldRow>
      )}
    </div>
  );
}
